import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useId, useListItem, useMergeRefs } from "@floating-ui/react";
import clsx from "clsx";
import { forwardRef, memo } from "react";
import { ButtonBase } from "./ButtonBase";
import { Stack } from "./Stack";
import { Typography } from "./Typography";
import { useColorable } from "../providers/ColorsProvider";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
import { useMenuDropdown } from "./Menu";
export const menuItemClasses = {
    root: "MinervaMenuItem-root",
    disabled: "MinervaMenuItem-disabled",
    selected: "MinervaMenuItem-selected",
};
export const menuItemSlots = {
    label: Typography,
    subLabel: Typography,
};
export const MenuItem = memo(forwardRef((props, forwardedRef) => {
    const { label, subLabel, value, startNode, endNode, density = "comfortable", color = "surface", colorSet, invertColors, slots, slotProps, selected, disabled, className, children, sx, onClick, disableOnClickCloseMenu = false, ...rest } = useDefaultProps({ component: "MenuItem", props });
    const colorStyles = useColorable({
        color,
        colorSet,
        invertColors: !invertColors,
    });
    const menuContext = useMenuDropdown();
    const id = useId();
    const item = useListItem({ label: disabled ? null : label });
    const isActive = item.index === menuContext?.activeIndex;
    return (_jsx(SlotsProvider, { baseSlots: menuItemSlots, slots: slots, children: ({ label: Label, subLabel: SubLabel }) => (_jsxs(ButtonBase, { ...menuContext?.getItemProps({}), ref: useMergeRefs([item.ref, forwardedRef]), id: id, value: value, disabled: disabled, className: clsx([
                className,
                menuItemClasses.root,
                disabled && menuItemClasses.disabled,
                selected && menuItemClasses.selected,
            ]), role: "menuitem", tabIndex: isActive ? 0 : -1, onClick: (event) => {
                event.stopPropagation();
                !disableOnClickCloseMenu && menuContext?.setOpen(false);
                onClick?.(event);
            }, sx: mergeSx({
                color: colorStyles.color,
                paddingY: density === "comfortable" ? 3 : density === "dense" ? 1 : 0,
                paddingLeft: density === "comfortable"
                    ? 2
                    : density === "dense"
                        ? startNode
                            ? 1
                            : 2
                        : 0,
                paddingRight: density === "comfortable"
                    ? 2
                    : density === "dense"
                        ? endNode
                            ? 1
                            : 2
                        : 0,
                borderRadius: (theme) => theme.radius.default,
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                textAlign: "left",
                [`&.${menuItemClasses.disabled}`]: {
                    color: (theme) => theme.palette.disabled.main,
                },
                [`&.${menuItemClasses.selected}`]: {
                    background: (theme) => theme.palette[color === "inherit" ? "surface" : color]
                        .container,
                    color: (theme) => theme.palette[color === "inherit" ? "surface" : color]
                        .onContainer,
                },
            }, sx), ...rest, children: [startNode, _jsxs(Stack, { alignItems: "flex-start", flex: 1, minWidth: 0, children: [_jsx(Label, { variant: "body2", truncate: true, width: "100%", fontWeight: selected ? "semibold" : "regular", ...slotProps?.label, children: label }), subLabel && (_jsx(SubLabel, { variant: "body3", width: "100%", ...slotProps?.subLabel, children: subLabel }))] }), endNode] })) }));
}));
//# sourceMappingURL=MenuItem.js.map